/**
 * Public one-time code reveal — the only place a recipient can obtain the full
 * gift-card code, and only after an emailed OTP.
 *
 * Client-side invariants:
 *  - the OTP, the reveal token and the code live in React state only: never in
 *    localStorage/sessionStorage, never in the URL, never in a QR;
 *  - the code is wiped on unmount, on tab hide and after a 2-minute timeout;
 *  - the *pending challenge* (opaque id + expiry + resend deadline) survives
 *    tab switches, re-renders and remounts via sessionStorage, so leaving to
 *    the mail app and coming back never hides the OTP input;
 *  - the resend cooldown only disables the resend button — it never controls
 *    the visibility of the OTP input;
 *  - all copy is generic — the UI never states whether an email exists.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Check, Loader2, ShieldCheck } from 'lucide-react';
import { giftOtpRequest, giftOtpVerify, giftRevealConsume } from '@/lib/giftCards';
import {
  clearChallenge,
  loadChallenge,
  saveChallenge,
  secondsUntil,
} from '@/lib/giftOtpSession';

const CODE_VISIBLE_MS = 2 * 60 * 1000;
const OTP_LENGTH = 6;
const DEFAULT_TTL_SECONDS = 10 * 60;
const DEFAULT_COOLDOWN_SECONDS = 60;

type Phase = 'idle' | 'sending' | 'awaiting_code' | 'verifying' | 'revealed' | 'blocked';

export function GiftRevealSection({ token }: { token: string }) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [message, setMessage] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [invalidCode, setInvalidCode] = useState(false);

  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const wipeTimer = useRef<number | null>(null);
  const submitting = useRef(false);

  const cooldown = secondsUntil(cooldownUntil, now);
  const challengeExpired = challengeId !== null && expiresAt > 0 && expiresAt <= now;
  const codeComplete = digits.every((d) => /^\d$/.test(d));
  // The verify button never depends on the resend cooldown.
  const canVerify = codeComplete && !!challengeId && !challengeExpired && phase !== 'verifying';

  /** Wipes secrets from React state only (never touches the pending challenge). */
  const wipeSecrets = useCallback(() => {
    setCode(null);
    setCopied(false);
    setDigits(Array(OTP_LENGTH).fill(''));
  }, []);

  /** Ends the challenge entirely: secrets + persisted routing state. */
  const endChallenge = useCallback(() => {
    wipeSecrets();
    setChallengeId(null);
    setExpiresAt(0);
    clearChallenge();
  }, [wipeSecrets]);

  // Restore a pending challenge for THIS gift token on mount (remount, back /
  // forward navigation inside the tab, or a real page reload). Entered digits
  // are intentionally not restored — only the visible, usable input is.
  useEffect(() => {
    const stored = loadChallenge(token);
    if (!stored) return;
    setChallengeId(stored.challengeId);
    setExpiresAt(stored.expiresAt);
    setCooldownUntil(stored.cooldownUntil);
    setPhase('awaiting_code');
    setMessage('הזינו את קוד האימות שנשלח במייל.');
  }, [token]);

  // Wipe the revealed code when the tab is hidden, but keep the challenge so
  // the user can switch to the mail app and return to the same input.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') return;
      wipeSecrets();
      setPhase((current) => {
        if (current === 'revealed') {
          const stored = loadChallenge(token);
          return stored ? 'awaiting_code' : 'idle';
        }
        return current;
      });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [token, wipeSecrets]);

  // Secrets never outlive the component.
  useEffect(() => {
    return () => {
      if (wipeTimer.current) window.clearTimeout(wipeTimer.current);
      setCode(null);
    };
  }, []);

  // Single ticker drives both the resend countdown and challenge expiry.
  useEffect(() => {
    if (!challengeId && cooldownUntil <= Date.now()) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [challengeId, cooldownUntil]);

  // Expiry cleanup: the input disappears only once the challenge is dead.
  useEffect(() => {
    if (!challengeExpired) return;
    if (phase !== 'awaiting_code' && phase !== 'verifying') return;
    endChallenge();
    setPhase('idle');
    setMessage('הקוד פג תוקף. בקשו קוד אימות חדש.');
  }, [challengeExpired, phase, endChallenge]);

  const request = async () => {
    setMessage(null);
    setPhase('sending');
    try {
      const res = await giftOtpRequest(token);
      // The request step is intentionally generic: no cooldown/lock/rate state.
      // A generic cooldown response returns the EXISTING challenge id, so the
      // same input keeps working instead of being reset.
      const ttl = Number(res.expires_in_seconds ?? DEFAULT_TTL_SECONDS) || DEFAULT_TTL_SECONDS;
      const resend = Number(res.resend_after_seconds ?? DEFAULT_COOLDOWN_SECONDS) || DEFAULT_COOLDOWN_SECONDS;
      const base = Date.now();
      const nextExpiresAt = base + ttl * 1000;
      const nextCooldownUntil = base + resend * 1000;

      setChallengeId(res.challenge_id);
      setExpiresAt(nextExpiresAt);
      setCooldownUntil(nextCooldownUntil);
      setNow(base);
      setDigits(Array(OTP_LENGTH).fill(''));
      saveChallenge(token, {
        challengeId: res.challenge_id,
        expiresAt: nextExpiresAt,
        cooldownUntil: nextCooldownUntil,
      });

      setPhase('awaiting_code');
      setMessage('שלחנו קוד אימות בת 6 ספרות לכתובת המייל של מקבל השובר.');
      // Focus only right after an explicit user action, so returning from the
      // mail app never force-opens the mobile keyboard.
      window.setTimeout(() => inputs.current[0]?.focus(), 50);
    } catch {
      setPhase(challengeId ? 'awaiting_code' : 'idle');
      setMessage('שגיאה זמנית. נסו שוב.');
    }
  };

  const submit = async (value: string) => {
    if (!challengeId || value.length !== OTP_LENGTH) return;
    if (submitting.current) return; // double-submit guard (click + Enter)
    submitting.current = true;
    setPhase('verifying');
    setInvalidCode(false);
    setMessage(null);
    try {
      const res = await giftOtpVerify(token, challengeId, value);
      if (!res.verified || !res.reveal_id || !res.reveal_token) {
        if (res.state === 'locked') {
          endChallenge();
          setPhase('blocked');
          setMessage('הקוד נחסם לאחר יותר מדי נסיונות. בקשו קוד חדש מאוחר יותר.');
          return;
        }
        if (res.state === 'expired' || res.state === 'replayed' || res.state === 'not_found') {
          endChallenge();
          setPhase('idle');
          setMessage('הקוד פג תוקף. בקשו קוד אימות חדש.');
          return;
        }
        // Wrong code: keep the digits on screen so the user can correct them.
        setPhase('awaiting_code');
        setInvalidCode(true);
        inputs.current[OTP_LENGTH - 1]?.focus();
        setMessage(
          typeof res.attempts_left === 'number'
            ? `הקוד שהוזן אינו נכון. תקנו את הספרות ונסו שוב — נותרו ${res.attempts_left} נסיונות.`
            : 'הקוד שהוזן אינו נכון. תקנו את הספרות ונסו שוב.',
        );
        return;
      }

      // Consume immediately: the capability is single-use and short-lived.
      const revealed = await giftRevealConsume(token, res.reveal_id, res.reveal_token);
      setCode(revealed.code);
      setChallengeId(null);
      setExpiresAt(0);
      clearChallenge();
      setDigits(Array(OTP_LENGTH).fill(''));
      setPhase('revealed');
      if (wipeTimer.current) window.clearTimeout(wipeTimer.current);
      wipeTimer.current = window.setTimeout(() => {
        wipeSecrets();
        setPhase('idle');
        setMessage('הקוד הוסתר מטעמי אבטחה. ניתן לבקש קוד אימות חדש.');
      }, CODE_VISIBLE_MS);
    } catch (e) {
      // Keep the challenge and the digits: a transient failure must not force a
      // new code request without an explanation.
      setPhase('awaiting_code');
      setMessage(e instanceof Error ? e.message : 'שגיאה זמנית. נסו שוב.');
    } finally {
      submitting.current = false;
    }
  };

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canVerify) return;
    void submit(digits.join(''));
  };

  const setDigit = (index: number, raw: string) => {
    setInvalidCode(false);
    const clean = raw.replace(/\D/g, '');
    if (!clean) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }
    const next = [...digits];
    // Support pasting the whole code into any box.
    for (let i = 0; i < clean.length && index + i < OTP_LENGTH; i++) next[index + i] = clean[i];
    setDigits(next);
    const focusAt = Math.min(index + clean.length, OTP_LENGTH - 1);
    inputs.current[focusAt]?.focus();
    // No auto-submit: the user explicitly presses "אימות הקוד".
  };


  const onKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
      inputs.current[index - 1]?.focus();
    }
  };

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setMessage('לא ניתן להעתיק אוטומטית. סמנו את הקוד והעתיקו ידנית.');
    }
  };

  const showOtpForm = phase === 'awaiting_code' || phase === 'verifying';

  return (
    <section
      dir="rtl"
      aria-labelledby="gift-reveal-title"
      className="lux-no-print w-full max-w-md rounded-3xl border border-white/15 bg-white/5 p-5 text-center backdrop-blur-sm"
    >
      <h2 id="gift-reveal-title" className="flex items-center justify-center gap-2 text-sm text-[#f6e7dd]">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" /> הצגת קוד למימוש באתר
      </h2>

      {phase !== 'revealed' && (
        <p className="mt-2 text-[12px] leading-relaxed text-[#f6e7dd]/70">
          לצפייה בקוד המלא נשלח קוד אימות חד-פעמי לכתובת המייל של מקבל השובר. מימוש בקליניקה אינו דורש קוד זה.
        </p>
      )}

      {(phase === 'idle' || phase === 'sending') && (
        <button
          type="button"
          onClick={request}
          disabled={phase === 'sending' || cooldown > 0}
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm text-[#f6e7dd] transition hover:bg-white/10 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd7c9]"
        >
          {phase === 'sending' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {cooldown > 0 ? `ניתן לבקש קוד חדש בעוד ${cooldown} שניות` : 'שליחת קוד אימות'}
        </button>
      )}

      {showOtpForm && (
        <form className="mt-4 space-y-3" data-testid="gift-otp-form" onSubmit={onSubmitForm} noValidate>
          <div dir="ltr" className="flex justify-center gap-2">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputs.current[i] = el;
                }}
                value={digit}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                inputMode="numeric"
                enterKeyHint="done"
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH}
                disabled={phase === 'verifying'}
                aria-label={`ספרה ${i + 1} מתוך ${OTP_LENGTH}`}
                aria-invalid={invalidCode || undefined}
                className="h-11 w-9 rounded-xl border border-white/25 bg-black/25 text-center text-lg text-[#f6e7dd] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd7c9] disabled:opacity-60"
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={!canVerify}
            aria-label="אימות הקוד"
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#ffd7c9] px-6 py-3 text-sm font-medium text-[#3b2318] transition hover:bg-[#ffe3d8] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd7c9] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1b1210]"
          >
            {phase === 'verifying' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {phase === 'verifying' ? 'מאמת…' : 'אימות הקוד'}
          </button>

          <button
            type="button"
            onClick={request}
            disabled={cooldown > 0 || phase === 'verifying'}
            className="inline-flex min-h-11 items-center justify-center px-2 text-[12px] text-[#f6e7dd]/70 underline underline-offset-4 disabled:no-underline disabled:opacity-60"
          >
            {cooldown > 0 ? `שליחה חוזרת בעוד ${cooldown} שניות` : 'שליחת קוד חדש'}
          </button>
        </form>

      )}

      {phase === 'revealed' && code && (
        <div className="mt-4 space-y-3">
          <div
            dir="ltr"
            className="select-all rounded-2xl border border-[#ffd7c9]/40 bg-black/35 px-4 py-3 font-mono text-base tracking-[0.18em] text-[#ffe9de]"
          >
            {code}
          </div>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2 text-sm text-[#f6e7dd] hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd7c9]"
          >
            {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            {copied ? 'הועתק' : 'העתקת הקוד'}
          </button>
          <p className="text-[11px] text-[#f6e7dd]/60">הקוד יוסתר אוטומטית לאחר שתי דקות. שמרו אותו במקום בטוח.</p>
        </div>
      )}

      {message && (
        <p aria-live="polite" className="mt-3 text-[12px] leading-relaxed text-[#f6e7dd]/80">
          {message}
        </p>
      )}
    </section>
  );
}
